import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { GenericListComponent } from '../../shared/generic-list/generic-list.component';
import { ColumnDefinition } from '../../../models/columnDefinition';
import { Customer } from '../../../models/customer';
import { CustomerService } from '../../../services/customer.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';

@Component({
  selector: 'app-customer-list',
  standalone: true,
  imports: [CommonModule, GenericListComponent, MatIconModule, MatButtonModule, NgxSpinnerModule],
  templateUrl: './customer-list.component.html',
  styleUrls: ['./customer-list.component.scss']
})
export class CustomerListComponent implements OnInit {
  @ViewChild(GenericListComponent) listComponent?: GenericListComponent<Customer>;

  customers: Customer[] = [];
  totalItems = 0;
  pageSize = 50;
  pageNumber = 1;
  sortBy = 'UserName';
  ascending = true;
  selectedCustomers: Customer[] = [];

  columnDefinitions: ColumnDefinition[] = [
    { propertyName: 'fullName', displayName: 'Customer Name' },
    // The e-mail is the username, so a separate Email column would only repeat it.
    { propertyName: 'userName', displayName: 'User Name' },
    { propertyName: 'isActive', displayName: 'Active', dataType: 'boolean' },
    { propertyName: 'canAccessDirectors', displayName: 'Directors Access', dataType: 'boolean' },
    { propertyName: 'isInternal', displayName: 'Internal', dataType: 'boolean' },
    { propertyName: 'countries', displayName: 'Countries', dataType: 'summary', labelKey: 'name', sortable: false },
    { propertyName: 'accessToYears', displayName: 'Measurements', dataType: 'summary', labelSort: 'desc', sortable: false },
    // Not sortable: lastLogin is derived from login-attempt history, not a sortable Customer column.
    { propertyName: 'lastLogin', displayName: 'Last Login', dataType: 'datetime', sortable: false }
  ];

  constructor(
    private customerService: CustomerService,
    private router: Router,
    private spinnerService: NgxSpinnerService,
    private confirmationDialogService: ConfirmationDialogService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadCustomers();
  }

  loadCustomers(): void {
    this.spinnerService.show();
    this.customerService.getCustomers(this.pageNumber, this.pageSize, this.sortBy, this.ascending).pipe(
      map(response => {
        this.totalItems = response.totalCount;
        this.spinnerService.hide();
        return response.data;
      }),
      catchError(error => {
        console.error('Error loading customers:', error);
        this.spinnerService.hide();
        return of([] as Customer[]);
      })
    ).subscribe(customers => {
      this.customers = customers;
    });
  }

  onCreateCustomer(): void {
    this.router.navigate(['/customers/create']);
  }

  onEditCustomer(customer: Customer): void {
    this.router.navigate(['/customers/edit', customer.id]);
  }

  onSelectionChange(selected: Customer[]): void {
    this.selectedCustomers = selected;
  }

  onBulkDelete(): void {
    if (this.selectedCustomers.length === 0) return;

    const count = this.selectedCustomers.length;
    this.confirmationDialogService.openConfirmationDialog(
      'Delete Customers',
      `Are you sure you want to delete ${count} customer${count === 1 ? '' : 's'}?`,
      'Delete',
      'Cancel'
    ).subscribe(confirmed => {
      if (!confirmed) return;

      const ids = this.selectedCustomers.map(c => c.id);
      this.spinnerService.show();
      this.customerService.deleteCustomers(ids).subscribe({
        next: (response) => {
          this.spinnerService.hide();
          this.listComponent?.clearSelection();
          this.selectedCustomers = [];
          if (response.failed.length === 0) {
            this.toast.success(`${response.deleted.length} customer${response.deleted.length === 1 ? '' : 's'} deleted`, {
              iconTheme: { primary: 'var(--mat-sys-primary)', secondary: '#FFF' }
            });
          } else {
            this.toast.warning(
              `Deleted ${response.deleted.length} of ${ids.length}. ${response.failed.length} failed.`
            );
          }
          this.loadCustomers();
        },
        error: (error) => {
          console.error('Error during bulk delete:', error);
          this.spinnerService.hide();
          this.toast.error('Failed to delete customers');
        }
      });
    });
  }

  onDeleteCustomer(customer: Customer): void {
    this.confirmationDialogService.openConfirmationDialog(
      'Delete Customer',
      `Are you sure you want to delete customer "${customer.fullName}"?`,
      'Delete',
      'Cancel'
    ).subscribe(confirmed => {
      if (!confirmed) return;

      this.spinnerService.show();
      this.customerService.deleteCustomer(customer.id).subscribe({
        next: () => {
          this.spinnerService.hide();
          this.toast.success('Customer deleted successfully', {
            iconTheme: { primary: 'var(--mat-sys-primary)', secondary: '#FFF' }
          });
          this.loadCustomers();
        },
        error: (error) => {
          console.error('Error deleting customer:', error);
          this.spinnerService.hide();
          this.toast.error('Failed to delete customer');
        }
      });
    });
  }

  onSortChange(sort: Sort): void {
    if (sort.direction) {
      this.sortBy = sort.active;
      this.ascending = sort.direction === 'asc';
    } else {
      this.sortBy = 'Id';
      this.ascending = true;
    }
    this.loadCustomers();
  }

  onPageChange(event: PageEvent): void {
    this.pageNumber = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadCustomers();
  }
}
